import React from 'react'
import { Animated, Pressable, View } from 'react-native'
import { useModalize } from 'react-native-modalize'
import { useTranslation } from 'react-i18next'

import BurgerIcon from '@common/assets/svg/BurgerIcon'
import NetworkStatusesIcon from '@common/assets/svg/NetworkStatusIcon'
import { isAmbireNext, isDev } from '@common/config/env'
import useNavigation from '@common/hooks/useNavigation'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import spacings from '@common/styles/spacings'
import flexboxStyles from '@common/styles/utils/flexbox'
import useHover from '@web/hooks/useHover'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'
import commonWebStyles from '@web/styles/utils/common'
import { getUiType } from '@web/utils/uiType'

import NetworkStatusesBottomSheet from '../NetworkStatusesBottomSheet'
import AccountButton from './AccountButton'
import { openInTab } from '@web/extension-services/background/webapi/tab'
import MaximizeIcon from '@common/assets/svg/MaximizeIcon'

import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import { ModeEnum } from '@web/contexts/appModeContext'
import useAppMode from '@web/hooks/useAppMode'

const { isPopup } = getUiType()

const expandViewTooltipId = 'expand-view-tooltip'
const SHOULD_DISPLAY_NETWORK_STATUSES = isAmbireNext || isDev

// Header icons sit on the mode gradient banner, so they're always white.
const ON_BANNER = '#fff'

const DashboardHeader = () => {
  const { t } = useTranslation()
  const { account } = useSelectedAccountControllerState()
  const [bindBurgerAnim, burgerAnimStyle] = useHover({ preset: 'opacity' })
  const [bindNetworkStatusesAnim, networkStatusesAnimStyle] = useHover({ preset: 'opacity' })
  const [bindMaximizeAnim, maximizeAnimStyle] = useHover({ preset: 'opacity' })
  const { navigate } = useNavigation()
  const { mode } = useAppMode()
  const {
    ref: networkStatusesSheetRef,
    open: openNetworkStatusesSheet,
    close: closeNetworkStatusesSheet
  } = useModalize()

  if (!account) return null

  return (
    <View
      style={[
        flexboxStyles.directionRow,
        flexboxStyles.alignCenter,
        flexboxStyles.flex1,
        commonWebStyles.contentContainer
      ]}
    >
      {SHOULD_DISPLAY_NETWORK_STATUSES && (
        <NetworkStatusesBottomSheet
          sheetRef={networkStatusesSheetRef}
          closeBottomSheet={closeNetworkStatusesSheet}
        />
      )}
      <View
        style={[flexboxStyles.directionRow, flexboxStyles.flex1, flexboxStyles.justifySpaceBetween]}
      >
        <View style={[flexboxStyles.directionRow]}>
          <AccountButton />
        </View>
        <View style={[flexboxStyles.directionRow]}>
          {SHOULD_DISPLAY_NETWORK_STATUSES && (
            <Pressable
              style={[spacings.phTy, spacings.pvTy, flexboxStyles.alignSelfCenter]}
              onPress={() => openNetworkStatusesSheet()}
              {...bindNetworkStatusesAnim}
            >
              <Animated.View style={networkStatusesAnimStyle}>
                <NetworkStatusesIcon
                  width={20}
                  height={20}
                  // All header icons sit on the mode gradient banner, so always white.
                  color={ON_BANNER}
                />
              </Animated.View>
            </Pressable>
          )}
          {isPopup && (
            <Pressable
              testID="dashboard-expand-view"
              style={[spacings.phTy, spacings.pvTy, flexboxStyles.alignSelfCenter]}
              onPress={() =>
                openInTab({
                  url: `tab.html#/${WEB_ROUTES.dashboard}?mode=${mode}`,
                  shouldCloseCurrentWindow: true
                })
              }
              {...bindMaximizeAnim}
            >
              <Animated.View style={maximizeAnimStyle}>
                <MaximizeIcon
                  width={18}
                  height={18}
                  // All header icons sit on the mode gradient banner, so always white.
                  color={ON_BANNER}
                  dataSet={createGlobalTooltipDataSet({
                    id: expandViewTooltipId,
                    content: t('Expand view')
                  })}
                />
              </Animated.View>
            </Pressable>
          )}
          <Pressable
            testID="dashboard-hamburger-btn"
            style={[spacings.mlTy, spacings.phTy, spacings.pvTy, flexboxStyles.alignSelfCenter]}
            onPress={() =>
              isPopup ? navigate(WEB_ROUTES.menu) : navigate(WEB_ROUTES.generalSettings)
            }
            {...bindBurgerAnim}
          >
            <Animated.View style={burgerAnimStyle}>
              <BurgerIcon
                // Settings button sits on the mode gradient banner, so always white.
                color={ON_BANNER}
                width={20}
                height={20}
              />
            </Animated.View>
          </Pressable>
        </View>
      </View>
    </View>
  )
}

export default React.memo(DashboardHeader)
